// Headless driver orchestration (fn-69.2, R1).
//
// Pure logic over a `HeadlessTransport`. One piece survives:
//
//   `bootstrapAndKickoff` — create the session, then send the kickoff
//   immediately (R1). fn-72 removed the separate fn-67 `<system-bootstrap>`
//   turn: a freshly created session is `pending` (sendable) and grounding
//   now rides the first user message (delivered server-side), so there is
//   NO bootstrap-ack to wait for, NO idle poll, NO sendable-status race,
//   and NO 409-retry. A conflict on the send is unexpected and surfaces as
//   "bootstrap-unavailable" (defensive).
//
// The approval-row resolver + the `/events` status classifier were deleted
// with the Archie headless page (their only consumer); the live consumers
// here are Preview's matter chat and the agent try-session, which need the
// kickoff sequence only.

import type { HeadlessCreateInput, HeadlessTransport } from './transport.js';

export interface BootstrapAndKickoffInput {
  readonly transport: HeadlessTransport;
  readonly create: HeadlessCreateInput;
  readonly kickoff: string;
}

export type BootstrapAndKickoffResult =
  | {
      readonly outcome: 'kickoff-sent';
      readonly sessionId: string;
    }
  | {
      readonly outcome: 'bootstrap-unavailable';
      readonly sessionId: string;
      /** The non-sendable status surfaced if the (unexpected) send conflicts. */
      readonly status: string;
    };

/**
 * Best-effort detection of a 409/conflict thrown by `sendMessage`. The CP
 * message route returns `409` when the session is no longer in a sendable
 * status; the BFF tRPC client surfaces that as a `CONFLICT` error carrying
 * `httpStatus: 409`. We narrow defensively across the shapes the client
 * can throw (and the raw message) so a status→send race degrades to
 * "bootstrap-unavailable" instead of an unhandled throw.
 */
function isConflictError(err: unknown): boolean {
  if (err === null || typeof err !== 'object') {
    return false;
  }
  const e = err as {
    data?: { httpStatus?: unknown; code?: unknown };
    shape?: { data?: { httpStatus?: unknown; code?: unknown } };
    message?: unknown;
  };
  if (e.data?.httpStatus === 409 || e.shape?.data?.httpStatus === 409) {
    return true;
  }
  if (e.data?.code === 'CONFLICT' || e.shape?.data?.code === 'CONFLICT') {
    return true;
  }
  return typeof e.message === 'string' && /\b409\b|conflict/i.test(e.message);
}

/**
 * Create the session, then send the kickoff — immediately. fn-72 removed the
 * separate fn-67 `<system-bootstrap>` turn: a freshly created session is
 * `pending` (sendable) and grounding now rides the first user message
 * (delivered server-side). So there is NO bootstrap-ack to wait for, NO idle
 * poll, NO sendable-status race, and NO 409-retry — the create→send→stream flow
 * is MA's native two-step lifecycle. The transient `409 session_busy` that drove
 * the old bootstrap-wait dance cannot occur here anymore (the row never leaves
 * `pending` between create and the first send).
 *
 * A conflict on the send is therefore unexpected; we stay defensive and surface
 * it as `bootstrap-unavailable` (re-reading the live status) rather than letting
 * an unhandled 409 escape — but the wait/poll/timeout machinery is gone.
 */
export async function bootstrapAndKickoff(
  input: BootstrapAndKickoffInput,
): Promise<BootstrapAndKickoffResult> {
  const { sessionId } = await input.transport.createSession(input.create);
  try {
    await input.transport.sendMessage(sessionId, input.kickoff);
  } catch (err) {
    if (isConflictError(err)) {
      const status = await input.transport.getSessionStatus(sessionId).catch(() => 'unknown');
      return { outcome: 'bootstrap-unavailable', sessionId, status };
    }
    throw err;
  }
  return { outcome: 'kickoff-sent', sessionId };
}
